import { useContext, useEffect, useState } from 'react';
import OAToolbar from '../../../components/widgets/OAToolbar';
import LedgerContext, { ILedgerContext } from '../context/LedgerContext';
import AccountBalance from './AccountBalance';
import BreadcrumbItem from './BreadcrumbItem';
import TransactionHistory from './TransactionHistory';
import CustomerLedgerCards from './CustomerLedgerCards';
import OrgProvider from '../../org-management/context/OrgProvider';
import { getOrganizationData } from '../../../helper/organizationStorage';

interface IListQueryParams {
  data: any;
}
const List = (props: IListQueryParams) => {
  const { queryParams, setQueryParams } = props?.data;

  const { getLedgerList, getInternalLedgerEntryList, getSupplierTransactions, getCurrentBalance, internalLedgerList, ledgerList }: ILedgerContext =
    useContext(LedgerContext);

  const [isRefresh, setIsRefresh] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Check if "all" organizations is selected
  const orgId = getOrganizationData()?.organizationId || '';
  const isAllOrgsSelected = orgId === 'all';

  const toggle = () => {
    setIsOpen(prev => !prev);
  };

  const refreshLedgerData = () => {
    getLedgerList();
    getInternalLedgerEntryList();
    if (queryParams?.customerId) {
      getSupplierTransactions();
    }
  };

  const getBalance = () => {
    setIsRefresh(true);
    getCurrentBalance(() => {
      refreshLedgerData();
      setIsRefresh(false);
    });
  };

  const handleTopupSuccess = () => {
    getInternalLedgerEntryList();
    if (queryParams?.customerId) {
      getSupplierTransactions();
    }
  };

  useEffect(() => {
    getBalance();
  }, []);

  useEffect(() => {
    if (queryParams?.dateRange[0] && queryParams?.dateRange[1]) {
      refreshLedgerData();
    }
  }, [queryParams]);

  // Set default customerId to "ONARRIVAL" when customerBreakdown is available
  useEffect(() => {
    if (internalLedgerList?.customerBreakdown?.length > 0 && !queryParams?.customerId) {
      setQueryParams((prev: any) => ({
        ...prev,
      }));
    }
  }, [internalLedgerList?.customerBreakdown]);


  const onChangeRangePicker = (range: any) => {
    setQueryParams((prev: any) => ({ ...prev, dateRange: range }));
  };

  const hasLedgerList = ledgerList?.length > 0;
  const isSupplierView = internalLedgerList?.supplierId;

  const renderTransactionHistory = () => (
    <OrgProvider>
      <TransactionHistory
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        onChangeRangePicker={onChangeRangePicker}
      />
    </OrgProvider>
  );

  const renderAccountBalance = () => (
    <AccountBalance
      toggle={toggle}
      isOpen={isOpen}
      isRefresh={isRefresh}
      getBalance={getBalance}
      internalLedgerList={internalLedgerList}
    />
  );

  if (isAllOrgsSelected) {
    return (
      <>
        <OAToolbar title={'Ledger'} breadcrumb={BreadcrumbItem()} />
        <div className='card'>
          <div className='card-body text-center py-10'>
            <div className='mb-5'>
              <i className='bi bi-exclamation-triangle-fill fs-3x text-warning'></i>
            </div>
            <h3 className='text-gray-800 mb-3'>Please Select an Organization</h3>
            <p className='text-gray-600 fs-6'>
              Ledger entries cannot be displayed when "All" organizations is selected.
              Please select a specific organization from the filter above to view ledger data.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (isSupplierView && !hasLedgerList) {
    return (
      <>
        <OAToolbar title={'Ledger'} breadcrumb={BreadcrumbItem()} />
        <CustomerLedgerCards data={internalLedgerList} onTopupSuccess={handleTopupSuccess} />
        {renderTransactionHistory()}
      </>
    );
  }

  // Always show AccountBalance and TransactionHistory for non-supplier views
  // This allows users to interact with "Add Topup Settlement" and "Upload Settlement" buttons even when there's no data
  return (
    <>
      <OAToolbar title={'Ledger'} breadcrumb={BreadcrumbItem()} />
      {renderAccountBalance()}
      {renderTransactionHistory()}
    </>
  );
};

export default List;